// Live's caption segmenter (#239, spec docs/specs/editor/captions.md, "The segmenter"): a video's
// timed words cut into captions, each caption's words cut into lines, and its box placed on the
// frame, by live's rules (read for behaviour, never copied). Ours differ where the spec says: the
// box is as tall as the renderer draws its lines (decision 14), the words are sorted first, and
// everything stays within the contract's bounds (decision 13). Pure: the import and #264's re-cut
// call it.
import { LINE_PITCH } from "../text-style";
import { MAX_CAPTION_WORDS, MAX_VIDEO_MS, cutWord, wordsIn, type CaptionBox, type CaptionWord, type TimedWord } from "./words";

export type Frequency = "punctuation_or_pause" | "single_word" | "time" | "random";
export type Capitalization = "none" | "uppercase" | "lowercase" | "title";

/** Live's paragraph settings (#264 makes them editable). */
export interface Paragraph {
  /** Lines per caption at most, 1 to 6. */
  quantity: number;
  frequency: Frequency;
  /** A silence this long or longer starts a new caption. */
  pauseS: number;
  /** Target characters per line. */
  characterCount: number;
  /** Words per caption with `single_word`. */
  wordsPerLine: number;
  /** A caption's length with `time`. */
  splitS: number;
  /** The box as wide as its longest line (else 84 % of the frame). */
  autofit: boolean;
  /** Drop each word's trailing . , ! ? ; : - — */
  hidePunctuation: boolean;
  capitalization: Capitalization;
}

/** Live's default preset's (a project whose captions were never styled): 1 line, punctuation or
 * pause, 0.4 s, 16 characters, 1 word, 2 s, no autofit, punctuation kept, as typed. */
export const DEFAULT_PARAGRAPH: Paragraph = {
  quantity: 1,
  frequency: "punctuation_or_pause",
  pauseS: 0.4,
  characterCount: 16,
  wordsPerLine: 1,
  splitS: 2,
  autofit: false,
  hidePunctuation: false,
  capitalization: "none",
};

/** How the caption's face measures, in live's pixels: a line's width, the face's ascent and
 * descent at the caption's size, and the style's size, padding, stroke width and shadow blur.
 * `width` is given a line's words joined by single spaces and answers its width as drawn. */
export interface CaptionMetrics {
  width(text: string): number;
  ascent: number;
  descent: number;
  size: number;
  padX: number;
  padY: number;
  stroke: number;
  shadowBlur: number;
  /** The style's line height (times live's 1.15 pitch); 1 when not given. */
  lineHeight?: number;
}

export interface Placement {
  horizontal: "left" | "center" | "right";
  vertical: "top" | "center" | "bottom";
}

/** Live's default: the box centred on the frame. */
export const CENTERED: Placement = { horizontal: "center", vertical: "center" };

/** One caption as cut: its time on the video, its words timed from its start with their lines,
 * and its box on the frame. */
export interface Segment {
  startMs: number;
  endMs: number;
  words: CaptionWord[];
  box: CaptionBox;
}

/** Live's limits: a caption closes once it lasts this long, and a gap this short is bridged. */
const MAX_CAPTION_MS = 3600;
const BRIDGE_MS = 120;
/** Live's slack on a caption's characters: its lines times the characters a line, times this. */
const CAPACITY_SLACK = 1.15;
/** A line's width at most, and the box's without autofit: this share of the frame's width. */
const MAX_WIDTH_SHARE = 0.84;
/** What live adds to the box's width and height besides twice the stroke and the shadow's blur. */
const BOX_EXTRA = 16;
/** Live's placement: the side margin's share of the frame's width, and the box's centre line's
 * share of its height at the top and the bottom. */
const SIDE_MARGIN = 0.06;
const TOP_LINE = 0.15;
const BOTTOM_LINE = 0.85;

/** A word ending a sentence: . ! ? or their full-width forms, perhaps a closing quote or bracket
 * after (live's). A comma, colon or dash does not end one. */
const SENTENCE_END = /[.!?。！？]["'”’」』)]?$/u;
/** A word ending a clause, which live's line split likes to end a line on. */
const CLAUSE_END = /[,;:，；：、\-–—]["'”’」』)]?$/u;
/** What Hide punctuation drops from a word's end (live's list). */
const TRAILING_PUNCTUATION = /[.,!?;:\-—]+$/u;

/** A word on its way through: its text as given (what the cuts read) and as shown. */
interface Word {
  raw: string;
  shown: string;
  startMs: number;
  endMs: number;
  emphasis: "positive" | "negative" | null;
}

/** A word's text in a case. */
function cased(text: string, capitalization: Capitalization): string {
  switch (capitalization) {
    case "none":
      return text;
    case "uppercase":
      return text.toUpperCase();
    case "lowercase":
      return text.toLowerCase();
    case "title":
      // The word's first character upper case, the rest as typed; a leading quote or bracket is
      // passed over ("world" is "World"), a leading digit is not (3rd stays 3rd).
      return text.replace(/^(\p{P}*)(\p{L})/u, (_, lead: string, letter: string) => lead + letter.toUpperCase());
  }
}

/** A word as shown: its trailing punctuation dropped when hidden, then its case set (which may
 * lengthen it: ß is SS, so it is cut again). */
function shownText(raw: string, paragraph: Paragraph): string {
  const text = paragraph.hidePunctuation ? raw.replace(TRAILING_PUNCTUATION, "") : raw;
  return cutWord(cased(text, paragraph.capitalization));
}

/**
 * The words in time order, within the video, each one word: one holding spaces is its pieces,
 * sharing its time evenly; an empty one is dropped. Times are whole ms, a word ending no earlier
 * than it starts and by 300 s. Live never sorted its words: an overlapping cue's words followed the
 * earlier cue's.
 */
function prepare(words: readonly TimedWord[], paragraph: Paragraph): Word[] {
  const pieces: TimedWord[] = [];
  for (const word of words) {
    if (!Number.isFinite(word.startMs)) continue;
    const end = Number.isFinite(word.endMs) ? Math.max(word.startMs, word.endMs) : word.startMs;
    // Split on the server's blank characters, control characters dropped (words.ts).
    const texts = wordsIn(word.text);
    const share = (end - word.startMs) / Math.max(1, texts.length);
    texts.forEach((text, at) =>
      pieces.push({
        text,
        startMs: word.startMs + at * share,
        endMs: word.startMs + (at + 1) * share,
        emphasis: word.emphasis ?? null,
      }),
    );
  }
  // A stable sort: words starting together keep the order they came in.
  pieces.sort((a, b) => a.startMs - b.startMs);
  const prepared: Word[] = [];
  for (const piece of pieces) {
    const startMs = Math.max(0, Math.round(piece.startMs));
    if (startMs >= MAX_VIDEO_MS) continue;
    const endMs = Math.min(MAX_VIDEO_MS, Math.max(startMs, Math.round(piece.endMs)));
    const raw = cutWord(piece.text);
    prepared.push({ raw, shown: shownText(raw, paragraph), startMs, endMs, emphasis: piece.emphasis ?? null });
  }
  return prepared;
}

/** A setting's whole number within its range. */
const within = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, Math.round(value)));

/**
 * Live's groups: the words in order, a caption closing after a word when the first of these holds
 * (live's order; the last word always closes one):
 * 1. the silence before the next word is the pause or longer;
 * 2. punctuation or pause: the word ends a sentence;
 * 3. single word: the caption holds its words;
 * 4. time: the caption lasts its length (first start to last end);
 * 5. random: the caption holds a fresh whole number of words from 2 to 5, drawn on every check;
 * 6. the caption lasts 3.6 s;
 * 7. the next word would take its text (single spaces) past the characters it may hold;
 * and ours: it holds the contract's 100 words. A caption's first word always joins it, so a word
 * longer than the characters is a caption alone. The checks read the words as given, so hiding
 * punctuation or changing case never moves a cut.
 */
function group(words: readonly Word[], paragraph: Paragraph, random: () => number): Word[][] {
  const quantity = within(paragraph.quantity, 1, 6);
  const capacity = Math.round(quantity * paragraph.characterCount * CAPACITY_SLACK);
  const pauseMs = Math.round(paragraph.pauseS * 1000);
  // A time cut needs a length: without one, live's 1 s or 0.9 s a line, whichever is longer.
  const splitMs = paragraph.splitS > 0 ? Math.round(paragraph.splitS * 1000) : Math.max(1000, quantity * 900);
  const groups: Word[][] = [];
  let current: Word[] = [];
  let chars = 0;
  const closes = (next: Word): boolean => {
    const first = current[0]!;
    const last = current[current.length - 1]!;
    const lasts = last.endMs - first.startMs;
    if (next.startMs - last.endMs >= pauseMs) return true;
    switch (paragraph.frequency) {
      case "punctuation_or_pause":
        if (SENTENCE_END.test(last.raw)) return true;
        break;
      case "single_word":
        if (current.length >= Math.max(1, paragraph.wordsPerLine)) return true;
        break;
      case "time":
        if (lasts >= splitMs) return true;
        break;
      case "random":
        if (current.length >= 2 + Math.min(3, Math.floor(random() * 4))) return true;
        break;
    }
    if (lasts >= MAX_CAPTION_MS) return true;
    if (chars + 1 + next.raw.length > capacity) return true;
    return current.length >= MAX_CAPTION_WORDS;
  };
  words.forEach((word, at) => {
    chars += (current.length > 0 ? 1 : 0) + word.raw.length;
    current.push(word);
    const next = words[at + 1];
    if (next === undefined || closes(next)) {
      groups.push(current);
      current = [];
      chars = 0;
    }
  });
  return groups;
}

/**
 * Each word's line, by live's balanced split: one line when the caption may hold one, holds one
 * word, cuts single words, or is short (its characters at most a line's, its width within the
 * frame's 84 %, three words at most); else the cheapest cut into 1 to `quantity` lines, fewer
 * lines on a tie. A line's cost (never below 0): its width past the limit, squared, times 50 (live's
 * pixels); its characters past a line's, squared, times 50, and 500 more when it holds two words or
 * more; a line other than the last, its characters short of a line's to the power 1.2, times 1.5;
 * a lone word on a line when the caption holds three or more, 100; ending a sentence -50, a clause
 * -30. Characters and punctuation read the words as given, widths the text as shown.
 */
function lineOfEach(words: readonly Word[], paragraph: Paragraph, maxWidth: number, metrics: CaptionMetrics): number[] {
  const count = words.length;
  const quantity = within(paragraph.quantity, 1, 6);
  const target = paragraph.characterCount;
  const oneLine = Array.from({ length: count }, () => 0);
  if (quantity <= 1 || count <= 1 || paragraph.frequency === "single_word") return oneLine;
  const rawOf = (from: number, to: number) => words.slice(from, to).map((word) => word.raw).join(" ");
  const widthOf = (from: number, to: number) => metrics.width(words.slice(from, to).map((word) => word.shown).join(" "));
  if (rawOf(0, count).length <= target && widthOf(0, count) <= maxWidth && count <= 3) return oneLine;

  const costs = new Map<number, number>();
  // The words from `from` up to `to` as one line.
  const lineCost = (from: number, to: number): number => {
    const key = from * (count + 1) + to;
    const known = costs.get(key);
    if (known !== undefined) return known;
    const held = to - from;
    const chars = rawOf(from, to).length;
    const width = widthOf(from, to);
    let cost = 0;
    if (width > maxWidth) cost += (width - maxWidth) ** 2 * 50;
    if (chars > target) cost += (chars - target) ** 2 * 50 + (held >= 2 ? 500 : 0);
    else if (to < count && chars < target) cost += (target - chars) ** 1.2 * 1.5;
    if (held === 1 && count >= 3) cost += 100;
    const end = words[to - 1]!.raw;
    if (SENTENCE_END.test(end)) cost -= 50;
    else if (CLAUSE_END.test(end)) cost -= 30;
    const floored = Math.max(0, cost);
    costs.set(key, floored);
    return floored;
  };

  // best[lines][to]: the cheapest way to put the first `to` words on `lines` lines; starts[lines][to]:
  // where its last line starts.
  const most = Math.min(quantity, count);
  const best: number[][] = [[0, ...Array.from({ length: count }, () => Infinity)]];
  const starts: number[][] = [[]];
  for (let lines = 1; lines <= most; lines++) {
    const row = Array.from({ length: count + 1 }, () => Infinity);
    const rowStarts = Array.from({ length: count + 1 }, () => 0);
    for (let to = lines; to <= count; to++) {
      for (let from = lines - 1; from < to; from++) {
        const cost = best[lines - 1]![from]! + lineCost(from, to);
        if (cost < row[to]!) {
          row[to] = cost;
          rowStarts[to] = from;
        }
      }
    }
    best.push(row);
    starts.push(rowStarts);
  }
  let chosen = 1;
  for (let lines = 2; lines <= most; lines++) if (best[lines]![count]! < best[chosen]![count]!) chosen = lines;
  const line = Array.from({ length: count }, () => 0);
  let to = count;
  for (let lines = chosen; lines >= 1; lines--) {
    const from = starts[lines]![to]!;
    for (let at = from; at < to; at++) line[at] = lines - 1;
    to = from;
  }
  return line;
}

/** A length rounded up to whole pixels, with no float noise (923.2 is 924, 924.0000001 is 924). */
const wholeUp = (value: number): number => Math.ceil(value - 1e-6);

/**
 * The caption's box: as wide as its widest line with autofit, else as the lines may be (84 % of the
 * frame); as tall as the renderer draws its lines (the padding, the face's ascent and descent, and
 * live's pitch between lines); each plus twice the stroke, twice the shadow's blur and 16 (live's),
 * rounded up. Placed by live's rules: 6 % of the frame's width in from a side, or centred; its
 * centre at 15 %, 50 % or 85 % of the frame's height.
 */
function boxOf(
  lineTexts: readonly string[],
  paragraph: Paragraph,
  frame: { width: number; height: number },
  metrics: CaptionMetrics,
  place: Placement,
): CaptionBox {
  const extra = 2 * metrics.stroke + 2 * metrics.shadowBlur + BOX_EXTRA;
  const textWidth = paragraph.autofit
    ? Math.max(0, ...lineTexts.map((text) => metrics.width(text)))
    : MAX_WIDTH_SHARE * frame.width;
  const pitch = Math.ceil(metrics.size * LINE_PITCH * (metrics.lineHeight ?? 1));
  const textHeight = 2 * metrics.padY + metrics.ascent + metrics.descent + (lineTexts.length - 1) * pitch;
  return placedBox(wholeUp(textWidth + extra), wholeUp(textHeight + extra), frame, place);
}

/** A box of `width` by `height` at its place on the frame (live's): left or right 6 % of the
 * frame's width in, or centred; its centre 15 %, 50 % or 85 % of the way down. */
export function placedBox(width: number, height: number, frame: { width: number; height: number }, place: Placement): CaptionBox {
  const margin = SIDE_MARGIN * frame.width;
  const x =
    place.horizontal === "left"
      ? margin
      : place.horizontal === "right"
        ? frame.width - margin - width
        : (frame.width - width) / 2;
  const share = place.vertical === "top" ? TOP_LINE : place.vertical === "bottom" ? BOTTOM_LINE : 0.5;
  return { x, y: share * frame.height - height / 2, width, height };
}

/** The words cut into captions by live's rules. `frame` is the project's frame in live's pixels
 * (`LIVE_FRAME`). */
export function segmentWords(
  words: readonly TimedWord[],
  paragraph: Paragraph,
  frame: { width: number; height: number },
  metrics: CaptionMetrics,
  place: Placement = CENTERED,
  random: () => number = seededRandom(words),
): Segment[] {
  // A word Hide punctuation empties is cut with the rest but not shown; a caption left with none
  // is dropped, and its time goes to its neighbours as a gap.
  const groups = group(prepare(words, paragraph), paragraph, random)
    .map((words) => words.filter((word) => word.shown !== ""))
    .filter((words) => words.length > 0);
  const maxWidth = MAX_WIDTH_SHARE * frame.width;
  const segments: Segment[] = [];
  // A caption starts no earlier than the one before ends.
  let floor = 0;
  groups.forEach((words, at) => {
    const first = words[0]!;
    const last = words[words.length - 1]!;
    // Past 300 s there is no room: a caption pushed there (below) is dropped, as a word starting
    // there is.
    const startMs = Math.max(first.startMs, floor);
    if (startMs >= MAX_VIDEO_MS) return;
    // Its end reaches the next caption's start across a gap of 0.12 s or less and never passes it
    // (so an earlier caption overlapping a later one is cut back to it, live's); a caption that
    // would last no time lasts 1 ms (live's lasted none and never showed), pushing the next by as
    // much.
    const next = groups[at + 1];
    let endMs = last.endMs;
    if (next !== undefined && next[0]!.startMs - endMs <= BRIDGE_MS) endMs = next[0]!.startMs;
    endMs = Math.min(MAX_VIDEO_MS, Math.max(endMs, startMs + 1));
    floor = endMs;
    const lengthMs = endMs - startMs;
    const line = lineOfEach(words, paragraph, maxWidth, metrics);
    let previousStart = 0;
    const captionWords = words.map((word, index): CaptionWord => {
      const wordStart = Math.max(previousStart, Math.min(lengthMs, Math.max(0, word.startMs - startMs)));
      previousStart = wordStart;
      const wordEnd = Math.min(lengthMs, Math.max(wordStart, word.endMs - startMs));
      return { text: word.shown, startMs: wordStart, endMs: wordEnd, line: line[index]!, emphasis: word.emphasis };
    });
    const lineTexts: string[] = [];
    for (const word of captionWords) {
      lineTexts[word.line] = lineTexts[word.line] === undefined ? word.text : `${lineTexts[word.line]} ${word.text}`;
    }
    segments.push({ startMs, endMs, words: captionWords, box: boxOf(lineTexts, paragraph, frame, metrics, place) });
  });
  return segments;
}

/**
 * Live's Random frequency draws afresh every time; ours draws from the words themselves, so the
 * same words cut the same way every time (a cut is the timeline's, and the timeline's edits are
 * pure). Mulberry32, seeded from the words' text and times.
 */
export function seededRandom(words: readonly TimedWord[]): () => number {
  let seed = 2166136261;
  for (const word of words) {
    for (const char of `${word.text}@${word.startMs}`) seed = Math.imul(seed ^ char.charCodeAt(0), 16777619) >>> 0;
  }
  return () => {
    seed = (seed + 0x6d2b79f5) >>> 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
